import React from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Users, ArrowRight } from "lucide-react";

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function ExperienceCard({ exp }) {
  const navigate = useNavigate();
  const slug = (exp.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const img = (exp.images && exp.images[0]) || "";
  const ageLabel = exp.age_min && exp.age_max ? `Ages ${exp.age_min}–${exp.age_max}` : (exp.grade_range || "");
  const memberLabel = exp.credit_eligible ? "Included*" : (exp.member_price === 0 ? "Included" : `$${exp.member_price}`);

  const STATUS = {
    open: { label: "Registration Open", cls: "bg-successBrand text-white" },
    limited: { label: "Limited Availability", cls: "bg-gold text-navy" },
    full: { label: "Full", cls: "bg-slate-500 text-white" },
    closed: { label: "Registration Closed", cls: "bg-slate-400 text-white" },
  };
  const TYPE_LABEL = { standard_local: "Local Experience", day_explorer: "H.E.A. Day Explorer", signature: "H.E.A. Signature" };
  const st = STATUS[exp.registration_status] || null;

  if (exp.coming_soon) {
    return (
      <div data-testid={`experience-card-${slug}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white opacity-95">
        <div className="relative h-52 overflow-hidden">
          <img src={img} alt={exp.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-navy/45" />
          <div className="absolute left-4 top-4">
            <span className="rounded-full bg-navy/90 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-wider text-white backdrop-blur">{exp.category}</span>
          </div>
          <div className="absolute inset-x-0 bottom-4 flex justify-center">
            <span className="rounded-full bg-gold px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-navy">Coming Soon</span>
          </div>
        </div>
        <div className="p-6">
          <h3 className="font-display text-xl font-bold text-navy">{exp.title}</h3>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`experience-card-${slug}`}
      onClick={() => navigate(`/experiences/${exp.id}`)}
      className="group cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white card-lift"
    >
      <div className="relative h-52 overflow-hidden">
        <img src={img} alt={exp.title} className="h-full w-full object-cover img-zoom" />
        <div className="absolute left-4 top-4">
          <span className="rounded-full bg-navy/90 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-wider text-white backdrop-blur">
            {exp.category}
          </span>
        </div>
        {st && (
          <div className="absolute right-4 top-4">
            <span className={`rounded-full px-3 py-1 text-[0.65rem] font-bold uppercase tracking-wider ${st.cls}`}>
              {st.label}
            </span>
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="font-display text-xl font-bold text-navy">{exp.title}</h3>
        <p className="mt-2 line-clamp-2 text-sm text-slateBrand">{exp.short_description}</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slateBrand">
          <span>{ageLabel}</span>
          <span className="text-slate-300">•</span>
          <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{exp.location}</span>
          <span className="text-slate-300">•</span>
          <span>{fmtDate(exp.date)}</span>
        </div>
        <div className="mt-5 flex items-end justify-between border-t border-slate-100 pt-4">
          <div className="text-sm">
            <div className="text-slateBrand">Member <span className="font-semibold text-navy">{memberLabel}</span></div>
            <div className="text-slateBrand">Non-member <span className="font-semibold text-navy">${exp.non_member_price}</span></div>
          </div>
          <span className="inline-flex items-center gap-1 font-display text-sm font-semibold text-brandblue">
            Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </div>
  );
}
